/**
 * TemperatureTable — the ONE temperature table for the cafeteria's Temperature
 * logs page and for the two production-record tasks (Transport logs' receipt
 * temperatures, Service records' service temperatures). One row per item, an
 * Item column with the HACCP class chip, then one column per reading the host
 * asks for: two on the page (Received, Served), one in each wizard.
 *
 * A cell is one of four things, decided by the host's data, never by the table:
 *   not offered  the existing "N/A" pill — a room-temperature item takes no
 *                reading (the record proves it was considered);
 *   submitted    the value Production Records holds, with a Lock tip (the
 *                Temperature logs page after the task is done);
 *   edit         `TempCheckpointInput` (the same presets + Custom the chefs
 *                use), with an Info tip when cafeteria mode logged a reading
 *                (that reading is what pre-filled the input);
 *   read         the value, with the same Info tip when it is a cafeteria reading.
 *
 * `frame="plain"` renders the bare table for a host that already draws the
 * card (the wizards' per-mealtime sections); the page uses the standard
 * bordered container. `loading` swaps the body for skeleton rows so another
 * day's or mealtime's numbers are never shown under this one.
 */

import { cn } from '@ordo/ui/cn'
import { Skeleton } from '@ordo/ui/skeleton'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@ordo/ui/table'

import type { TemperatureReadingWire, TemperatureSubmissionWire } from '@/api/cafeteria-temperatures'
import { CafeteriaModeTip } from '@/components/cafeteria/cafeteria-mode-tip'
import { type TempCheckpointClass, TempCheckpointInput } from '@/components/tasks/temp-checkpoint-input'
import { formatTemperatureF, temperatureCellInvalid } from '@/lib/cafeteria/temperature-edit'

export type TemperatureClass = 'HOT' | 'CHILLED' | 'AMBIENT'

export const TEMP_CLASS_LABEL: Record<TemperatureClass, string> = {
  HOT: 'Hot',
  CHILLED: 'Chilled',
  AMBIENT: 'Room temp',
}
export const TEMP_CLASS_CHIP: Record<TemperatureClass, string> = {
  HOT: 'bg-orange-100 text-orange-700',
  CHILLED: 'bg-sky-100 text-sky-700',
  AMBIENT: 'bg-stone-100 text-stone-600',
}

/** Shelf-stable: visible so the record shows the item was considered, but there is no reading to take. */
export const NO_READING_LABEL = 'N/A — room temp'
export const TEMPERATURE_RANGE_HINT = 'Enter a value between -20°F and 250°F.'
export const TEMPERATURE_TABLE_SKELETON_ROWS = 6

export interface TemperatureTableColumn {
  key: string
  header: string
  /** Completes the input's accessible name: `${row.label} ${inputLabel}`. */
  inputLabel: string
}

export interface TemperatureTableCell {
  offered: boolean
  /** The value shown, or edited: the host's draft in edit mode, the stored/submitted value otherwise. */
  value: number | null
  /** What cafeteria mode logged for this cell — the Info tip's content. */
  reading: TemperatureReadingWire | null
  /** What Production Records holds; non-null locks the cell behind a Lock tip. */
  submitted: TemperatureSubmissionWire | null
}

export interface TemperatureTableRow {
  key: string
  label: string
  sublabel?: string | null
  temperatureClass: TemperatureClass | null
  cells: Record<string, TemperatureTableCell>
}

export interface TemperatureTableEdit {
  onChange: (rowKey: string, columnKey: string, value: number | null) => void
  /** A save in flight: the inputs stay visible but locked. */
  disabled: boolean
}

export interface TemperatureTableProps {
  columns: ReadonlyArray<TemperatureTableColumn>
  rows: ReadonlyArray<TemperatureTableRow>
  /** Skeleton rows instead of data — the cold load, or a day / mealtime switch still loading. */
  loading: boolean
  /** Null = the read state. */
  edit: TemperatureTableEdit | null
  emptyText: string
  frame?: 'card' | 'plain'
}

/** A temperature column is sized for the preset select + the custom box (`TempCheckpointInput`). */
const TEMP_COLUMN_CLASS = 'w-[15rem] min-w-[13rem]'

export function TemperatureTable({ columns, rows, loading, edit, emptyText, frame = 'card' }: TemperatureTableProps) {
  const table = (
    // `min-w-0` lets the table fit its container (item names wrap) instead of the primitive's max-content scroll.
    <Table className="min-w-0" aria-busy={loading || undefined}>
      <TableHeader>
        <TableRow>
          <TableHead>Item</TableHead>
          {columns.map((column) => (
            <TableHead key={column.key} className={TEMP_COLUMN_CLASS}>
              {column.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading ? (
          <SkeletonRows columnCount={columns.length} />
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length + 1}
              className="whitespace-normal py-8 text-center text-sm text-muted-foreground">
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => <TemperatureTableRowView key={row.key} row={row} columns={columns} edit={edit} />)
        )}
      </TableBody>
    </Table>
  )
  return frame === 'card' ? <TableContainer>{table}</TableContainer> : table
}

function TemperatureTableRowView({
  row,
  columns,
  edit,
}: {
  row: TemperatureTableRow
  columns: ReadonlyArray<TemperatureTableColumn>
  edit: TemperatureTableEdit | null
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-normal font-medium">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span>{row.label}</span>
          {row.temperatureClass ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                TEMP_CLASS_CHIP[row.temperatureClass],
              )}>
              {TEMP_CLASS_LABEL[row.temperatureClass]}
            </span>
          ) : null}
        </div>
        {row.sublabel ? <div className="text-xs font-normal text-muted-foreground">{row.sublabel}</div> : null}
      </TableCell>
      {columns.map((column) => (
        <TableCell key={column.key} className={cn(TEMP_COLUMN_CLASS, 'whitespace-normal')}>
          <TemperatureCellView row={row} column={column} edit={edit} />
        </TableCell>
      ))}
    </TableRow>
  )
}

function TemperatureCellView({
  row,
  column,
  edit,
}: {
  row: TemperatureTableRow
  column: TemperatureTableColumn
  edit: TemperatureTableEdit | null
}) {
  const cell = row.cells[column.key]
  if (!cell || !cell.offered) {
    return (
      <span className="inline-flex h-9 items-center rounded-md bg-muted px-3 text-sm text-muted-foreground">
        {NO_READING_LABEL}
      </span>
    )
  }
  if (cell.submitted) {
    const differs = cell.reading !== null && cell.reading.value !== cell.submitted.value
    return (
      <span className="inline-flex items-center gap-1 tabular-nums">
        <Value value={cell.submitted.value} />
        <CafeteriaModeTip
          kind="submitted"
          at={cell.submitted.at}
          byName={cell.submitted.byName}
          cafeteriaLabel={differs && cell.reading ? formatTemperatureF(cell.reading.value) : null}
        />
      </span>
    )
  }
  const loggedTip = cell.reading ? (
    <CafeteriaModeTip
      kind="logged"
      label={formatTemperatureF(cell.reading.value)}
      loggedAt={cell.reading.loggedAt}
      loggedByName={cell.reading.loggedByName}
    />
  ) : null
  if (edit) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <TempCheckpointInput
            value={cell.value}
            onChange={(next) => edit.onChange(row.key, column.key, next)}
            ariaLabel={`${row.label} ${column.inputLabel}`}
            temperatureClass={tempCheckpointClass(row.temperatureClass)}
            disabled={edit.disabled}
          />
          {loggedTip}
        </div>
        {temperatureCellInvalid(cell.value) ? (
          <p className="text-xs text-destructive">{TEMPERATURE_RANGE_HINT}</p>
        ) : null}
      </div>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <Value value={cell.value} />
      {loggedTip}
    </span>
  )
}

/** A reading; an unknown value reads as a muted dash, never a fake number. */
function Value({ value }: { value: number | null }) {
  const text = formatTemperatureF(value)
  return value === null ? <span className="text-muted-foreground">{text}</span> : <>{text}</>
}

function tempCheckpointClass(temperatureClass: TemperatureClass | null): TempCheckpointClass {
  switch (temperatureClass) {
    case 'HOT':
      return 'hot'
    case 'CHILLED':
      return 'chilled'
    case 'AMBIENT':
      return 'ambient'
    default:
      return null
  }
}

function SkeletonRows({ columnCount }: { columnCount: number }) {
  return (
    <>
      {Array.from({ length: TEMPERATURE_TABLE_SKELETON_ROWS }, (_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-56 max-w-full" />
          </TableCell>
          {Array.from({ length: columnCount }, (_, j) => (
            <TableCell key={j} className={TEMP_COLUMN_CLASS}>
              <Skeleton className="h-9 w-40" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
